// frontend/modules/service-metrics/hooks/useServiceMetrics.ts
import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useServiceMetricsStore } from '../stores/service-metrics-store';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { useTheme } from '@/components/common/ThemeProvider';
import {
    useGetAllServiceNames,
    useGetTopServices,
    useGetServicesWithFailures,
    useGetServiceSummary,
    useGetServiceHealth,
    useGetServiceLogs,
    useGetServiceMetrics,
    useCleanupOldMetrics,
    getGetAllServiceNamesQueryKey,
    getGetTopServicesQueryKey,
    getGetServicesWithFailuresQueryKey,
} from '@/shared/api/client/service-metrics';

export const useServiceMetrics = () => {
    const { dark } = useTheme();
    const queryClient = useQueryClient();
    const {
        serviceNames,
        metrics,
        selectedServiceSummary,
        serviceHealth,
        serviceLogs,
        topServices,
        servicesWithFailures,
        isLoading,
        error,
        selectedService,
        filters,
        currentPage,
        pageSize,
        totalElements,
        setServiceNames,
        setMetrics,
        setSelectedServiceSummary,
        setServiceHealth,
        setServiceLogs,
        setTopServices,
        setServicesWithFailures,
        setIsLoading,
        setError,
        setSelectedService,
        setFilters,
        setCurrentPage,
        setPageSize,
        setTotalElements,
    } = useServiceMetricsStore();

    // Query hooks
    const serviceNamesQuery = useGetAllServiceNames();
    const topServicesQuery = useGetTopServices({ days: filters.days || 7, limit: 10 });
    const failuresQuery = useGetServicesWithFailures({ days: filters.days || 7 });
    
    // Conditional queries for selected service
    const serviceSummaryQuery = useGetServiceSummary(
        selectedService || '',
        { days: filters.days || 7 },
        { query: { enabled: !!selectedService } }
    );
    const serviceHealthQuery = useGetServiceHealth(
        selectedService || '',
        undefined,
        { query: { enabled: !!selectedService } }
    );
    const serviceLogsQuery = useGetServiceLogs(
        selectedService || '',
        { page: currentPage, size: pageSize },
        { query: { enabled: !!selectedService } }
    );
    const serviceMetricsQuery = useGetServiceMetrics(
        selectedService || '',
        { query: { enabled: !!selectedService } }
    );

    // Cleanup mutation
    const cleanupMutation = useCleanupOldMetrics({
        mutation: {
            onSuccess: () => {
                showSuccessNotification('Success', 'Cleaned up old metrics', dark);
                queryClient.invalidateQueries({ queryKey: getGetAllServiceNamesQueryKey() });
                queryClient.invalidateQueries({ queryKey: getGetTopServicesQueryKey() });
            },
            onError: () => {
                showErrorNotification('Error', 'Failed to cleanup old metrics', dark);
            }
        }
    });

    // Update store when queries complete
    useEffect(() => {
        if (serviceNamesQuery.data) {
            setServiceNames(serviceNamesQuery.data);
        }
    }, [serviceNamesQuery.data, setServiceNames]);

    useEffect(() => {
        if (topServicesQuery.data) {
            setTopServices(topServicesQuery.data);
        }
    }, [topServicesQuery.data, setTopServices]);

    useEffect(() => {
        if (failuresQuery.data) {
            setServicesWithFailures(failuresQuery.data);
        }
    }, [failuresQuery.data, setServicesWithFailures]);

    useEffect(() => {
        if (serviceSummaryQuery.data) {
            setSelectedServiceSummary(serviceSummaryQuery.data as any);
        }
    }, [serviceSummaryQuery.data, setSelectedServiceSummary]);

    useEffect(() => {
        if (serviceHealthQuery.data) {
            setServiceHealth(serviceHealthQuery.data as any);
        }
    }, [serviceHealthQuery.data, setServiceHealth]);

    useEffect(() => {
        if (serviceLogsQuery.data) {
            setServiceLogs(serviceLogsQuery.data.content || []);
            setTotalElements(serviceLogsQuery.data.totalElements || 0);
        }
    }, [serviceLogsQuery.data, setServiceLogs, setTotalElements]);

    useEffect(() => {
        if (serviceMetricsQuery.data) {
            setMetrics(serviceMetricsQuery.data);
        }
    }, [serviceMetricsQuery.data, setMetrics]);

    // Combined loading state
    const combinedLoading = serviceNamesQuery.isLoading || 
        topServicesQuery.isLoading || 
        failuresQuery.isLoading ||
        serviceSummaryQuery.isLoading ||
        serviceHealthQuery.isLoading ||
        serviceLogsQuery.isLoading ||
        cleanupMutation.isPending;

    // Select a service
    const selectService = useCallback((serviceName: string) => {
        setSelectedService(serviceName);
        setCurrentPage(0);
    }, [setSelectedService, setCurrentPage]);

    // Refresh data
    const refreshData = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: getGetAllServiceNamesQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetTopServicesQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetServicesWithFailuresQueryKey() });
        if (selectedService) {
            serviceSummaryQuery.refetch();
            serviceHealthQuery.refetch();
            serviceLogsQuery.refetch();
        }
    }, [queryClient, selectedService, serviceSummaryQuery, serviceHealthQuery, serviceLogsQuery]);

    // Cleanup old metrics
    const cleanupOldMetrics = useCallback((retentionDays: number = 90) => {
        cleanupMutation.mutate({ params: { retentionDays } });
    }, [cleanupMutation]);

    // Fetch service logs with pagination
    const fetchServiceLogs = useCallback((serviceName: string, page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    }, [setCurrentPage, setPageSize]);

    return {
        // State
        serviceNames,
        metrics,
        selectedServiceSummary,
        serviceHealth,
        serviceLogs,
        topServices,
        servicesWithFailures,
        isLoading: combinedLoading,
        error,
        selectedService,
        filters,
        currentPage,
        pageSize,
        totalElements,
        
        // Actions
        selectService,
        refreshData,
        cleanupOldMetrics,
        fetchServiceLogs,
        setFilters,
        setSelectedService,
        setCurrentPage,
        setPageSize,
    };
};
